import type { PreviewLayer } from '@/components/preview/preview-static-layer-renderer';
import { isPreviewLayersArray } from '@/helpers/preview/preview-layer-utils';
import { resolveSequencedDwellMs } from '@/helpers/preview/resolve-sequenced-dwell-ms.helper';

export type PreviewSequencedPlaybackSettings = {
  loopEnabled: boolean;
  dwellMs?: number;
};

function applySequencedPlaybackToLayer(
  layer: PreviewLayer,
  itemId: string,
  settings: PreviewSequencedPlaybackSettings,
): PreviewLayer {
  if (layer.componentId !== 'imageSequence' && layer.componentId !== 'presentationSlides') {
    return layer;
  }
  if (layer.itemId !== itemId) {
    return layer;
  }

  const nextData: Record<string, string> = { ...layer.data };
  if (settings.loopEnabled) {
    nextData.autoLoop = 'true';
    nextData.dwellMs = String(resolveSequencedDwellMs(settings.dwellMs));
    delete nextData.autoAdvance;
  } else {
    delete nextData.autoLoop;
    delete nextData.autoAdvance;
    delete nextData.dwellMs;
  }
  return {
    ...layer,
    data: nextData,
  };
}

export function applySequencedPlaybackToPreviewLayers(
  layers: PreviewLayer[],
  itemId: string,
  settings: PreviewSequencedPlaybackSettings,
): PreviewLayer[] {
  return layers.map((layer) => applySequencedPlaybackToLayer(layer, itemId, settings));
}

export function enrichPlayerSyncDataWithSequencedPlayback(
  playerSyncData: Record<string, unknown>,
  options: { mediaId: string; loopEnabled: boolean; dwellMs?: number },
): Record<string, unknown> {
  const layers = playerSyncData.layers;
  if (!isPreviewLayersArray(layers)) {
    return playerSyncData;
  }

  const itemId = `media:${options.mediaId}`;
  return {
    ...playerSyncData,
    layers: applySequencedPlaybackToPreviewLayers(layers, itemId, {
      loopEnabled: options.loopEnabled,
      dwellMs: options.dwellMs,
    }),
  };
}
